import React, { useEffect, useMemo } from "react";
import hoistNonReactStatics from "hoist-non-react-statics";
import { useStore } from "react-redux";
import { useLocation, useParams } from "react-router-dom";
import qs from "query-string";
import defaults from "lodash/defaults";

import { handleFetchError } from "@/util/errorHandler";

import ErrorLayout from "@/components/errorLayout";
import { ContextStorage } from "@/libs/contextStorage";

import { isServer, useAppDispatch, useAppSelector } from "../../redux/store";
import {
  setIsServerLoadedReduxState,
  showLoaders,
  hideLoaders
} from "../../redux/actions/common.action";
import { useLanguage } from "../../hooks";
import { withClientOnlyRender } from "../../hocs";

import {
  loadCommonGlobalData,
  loadClientGlobalData
} from "./globalDataLoading";
import { usePageInitialProps } from "./context";
import { usePageTracking } from "./hooks";

import {
  ServerLoadedPage,
  InitializationConfig,
  PageLoadStrategyDataArgument,
  internalPageDataBridgeInitialProps
} from "./types";

const withPageInitialLoad = <Props = {}, InitialProps = {}>({
  page: PageComponent,
  pageName,
  strategy,
  ssr: ssrEnabled,
  loader: isLoaderInUse = true,
  defaultInitialProps = {},
  loaderConfig
}: InitializationConfig<Props, InitialProps>) => {
  const EnhancedComponent: ServerLoadedPage<Props & InitialProps> = props => {
    const { initialProps, setInitialProps } = usePageInitialProps<
      InitialProps & internalPageDataBridgeInitialProps
    >();

    const store = useStore();
    const params = useParams<Record<string, string>>();
    const location = useLocation();
    const language = useLanguage();

    const dispatch = useAppDispatch();

    const isServerLoadedReduxState = useAppSelector(
      state => state.common.isServerLoadedReduxState
    );
    let loadedPageName =
      typeof pageName === "function" ? pageName(props) : pageName;

    usePageTracking({ name: loadedPageName });

    useEffect(() => {
      (async () => {
        const data: PageLoadStrategyDataArgument = {
          store,
          params,
          query: location.search
            ? (qs.parse(location.search) as Record<string, string>)
            : {},
          cookies: ContextStorage.cookies.getAll(),
          language,
          isServer,
          url: location.pathname
        };

        if (isServerLoadedReduxState) {
          await loadClientGlobalData(data);
          isGlobalDataLoaded = true;

          return dispatch(setIsServerLoadedReduxState(false));
        }

        const shouldStartLoading = Boolean(!isGlobalDataLoaded || strategy);
        if (!shouldStartLoading) return;
        try {
          if (isLoaderInUse) dispatch(showLoaders(loaderConfig));

          if (!isGlobalDataLoaded) {
            await loadCommonGlobalData(data);
            await loadClientGlobalData(data);

            isGlobalDataLoaded = true;
          }

          const initialProps = strategy && (await strategy(data));
          if (initialProps !== undefined) {
            setInitialProps(initialProps as InitialProps);
          }
        } catch (error) {
          handleFetchError(error, dispatch);
          setInitialProps({
            __PAGE_DATA_BRIDGE: {
              hasError: true
            }
          } as InitialProps);
        } finally {
          if (isLoaderInUse) dispatch(hideLoaders());
        }
      })();
    }, [location]);

    const { componentProps, bridgeProps } = useMemo(() => {
      const { __PAGE_DATA_BRIDGE: bridgeProps = {}, ...componentProps } =
        initialProps || {};

      if (!defaultInitialProps)
        return {
          componentProps,
          bridgeProps
        };

      return {
        componentProps: defaults(
          { ...(componentProps || {}) },
          defaultInitialProps
        ),
        bridgeProps
      };
    }, [initialProps]);

    const hasError = Boolean(bridgeProps.hasError);
    useEffect(() => {
      return () => {
        if (hasError) {
          setInitialProps({
            __PAGE_DATA_BRIDGE: {
              hasError: false
            }
          } as InitialProps);
        }
      };
    }, [location, hasError]);

    if (hasError) return <ErrorLayout />;
    return <PageComponent {...props} {...componentProps} />;
  };

  EnhancedComponent.ssrConfig = {
    initialLoad: async data => {
      try {
        await loadCommonGlobalData(data);
        if (!ssrEnabled) return;

        return await strategy(data);
      } catch (error) {
        handleFetchError(error, data.store.dispatch);

        return {
          __PAGE_DATA_BRIDGE: {
            hasError: true
          }
        };
      }
    },
    ssr: ssrEnabled
  };

  hoistNonReactStatics(EnhancedComponent, PageComponent);

  return ssrEnabled
    ? EnhancedComponent
    : withClientOnlyRender(EnhancedComponent);
};

let isGlobalDataLoaded = false;

export default withPageInitialLoad;
